import { Component, ChangeDetectionStrategy, input, output, signal, inject, ElementRef, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { XmedicalUiModule } from '@xmedical/ui';
import { FileUploaderService } from '../../services/file-uploader.service';
import { ImageViewComponent } from '../image-view/image-view.component';

export type Orientation = 'landscape' | 'portrait' | 'square';

@Component({
  selector: 'xmed-image-uploader',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, XmedicalUiModule, ImageViewComponent],
  templateUrl: './image-uploader.component.html',
  styleUrl: './image-uploader.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageUploaderComponent {
  private readonly uploader = inject(FileUploaderService);
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  orientation = input<Orientation>('landscape');
  url = input<string>('');
  legacy = input(false);

  urlChange = output<string>();

  uploading = signal(false);
  private file: File | null = null;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.file = input.files[0];
      this.upload();
    }
  }

  openFileInput(event: MouseEvent): void {
    event.stopPropagation();
    this.fileInput()?.nativeElement.click();
  }

  removeImage(): void {
    this.urlChange.emit('');
  }

  private get fileKey(): string {
    if (!this.file) return '';
    const dynamicValue = this.uploader.generateUuid();
    return `uploads/practices/files/logos/${encodeURI(dynamicValue)}/${this.file.name}`;
  }

  private upload(): void {
    if (this.legacy()) {
      this.legacyUpload();
    } else {
      this.cloudFlareUpload();
    }
  }

  private legacyUpload(): void {
    if (!this.file) return;
    this.uploading.set(true);
    const fileKey = this.fileKey;

    this.uploader.getSignedUrl(fileKey, 'PUT', 300).subscribe({
      next: (signedUrl) => {
        this.uploader.uploadFile(this.file!, signedUrl).subscribe({
          next: () => {
            this.urlChange.emit(fileKey);
            this.uploading.set(false);
          },
          error: (err) => {
            console.error('Upload failed:', err);
            this.uploading.set(false);
          }
        });
      },
      error: (err) => {
        console.error('Failed to get signed URL:', err);
        this.uploading.set(false);
      }
    });
  }

  private cloudFlareUpload(): void {
    if (!this.file) return;
    this.uploading.set(true);
    const fileKey = this.fileKey;

    this.uploader.getSignedAssetsUrl(fileKey, 'PUT', 300).subscribe({
      next: (signedUrl) => {
        this.uploader.uploadFile(this.file!, signedUrl).subscribe({
          next: () => {
            this.urlChange.emit(fileKey);
            this.uploading.set(false);
          },
          error: (err) => {
            console.error('Upload failed:', err);
            this.uploading.set(false);
          }
        });
      },
      error: (err) => {
        console.error('Failed to get signed URL:', err);
        this.uploading.set(false);
      }
    });
  }
}
